import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  animate,
  motion,
  AnimatePresence,
  useMotionValue,
  useSpring,
  useTransform,
  MotionValue,
} from 'framer-motion';
import {
  Box,
  Typography,
  IconButton,
  Button,
  InputBase,
} from '@mui/material';
import {
  Check,
  Copy,
  X,
  RotateCcw,
  ChevronDown,
} from 'lucide-react';
import {
  DEFAULT_PRIMARY_COLOR,
  getContrastTextColor,
  getContrastRatio,
  hslToHex,
  hexToHsl,
  isValidHex,
  normalizeHex,
} from '../../../utils/theme/brandTheme';

const MotionBox = motion(Box);

/**
 * ==============   Utils & Math   ================
 */

function calculateAngle(index: number, totalInRing: number): number {
  return (index / totalInRing) * Math.PI * 2;
}

function calculateBasePosition(angle: number, radius: number) {
  return {
    x: Math.cos(angle) * radius,
    y: Math.sin(angle) * radius,
  };
}

function calculateHue(angle: number): number {
  const hueDegrees = (angle * 180) / Math.PI - 90 - 180;
  return ((hueDegrees % 360) + 360) % 360;
}

/**
 * Custom pointer position tracker using MotionValues
 */
function usePointerPosition() {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  useEffect(() => {
    const handlePointerMove = (e: PointerEvent | MouseEvent) => {
      x.set(e.clientX);
      y.set(e.clientY);
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    return () => window.removeEventListener('pointermove', handlePointerMove);
  }, [x, y]);

  return { x, y };
}

interface SpringConfig {
  damping: number;
  stiffness: number;
}

interface ColorDotProps {
  ring: number;
  index: number;
  totalInRing: number;
  centerX: number;
  centerY: number;
  pointerX: MotionValue<number>;
  pointerY: MotionValue<number>;
  pushMagnitude: number;
  pushSpring: SpringConfig;
  radius: number;
  selectedColor: string | null;
  setSelectedColor: (color: string | null) => void;
}

function ColorDot({
  ring,
  index,
  totalInRing,
  centerX,
  centerY,
  pointerX,
  pointerY,
  pushMagnitude,
  pushSpring,
  radius,
  selectedColor,
  setSelectedColor,
}: ColorDotProps) {
  const baseRadius = ring * 20;
  const angle = calculateAngle(index, totalInRing);
  const { x: baseX, y: baseY } = calculateBasePosition(angle, baseRadius);

  let color = 'hsl(0, 0%, 100%)';
  let normalizedHue = 0;
  if (ring !== 0) {
    normalizedHue = calculateHue(angle);
    color =
      ring === 1
        ? `hsl(${normalizedHue}, 60%, 85%)`
        : `hsl(${normalizedHue}, 90%, 60%)`;
  }

  const pushDistance = useTransform(
    [pointerX, pointerY],
    ([px, py]: number[]) => {
      if (centerX === 0 || centerY === 0) return 0;

      const dx = px - centerX;
      const dy = py - centerY;
      const distanceFromCenter = Math.sqrt(dx * dx + dy * dy);

      if (distanceFromCenter > radius + 40) return 0;

      const dotX = centerX + baseX;
      const dotY = centerY + baseY;

      const cursorToDotX = dotX - px;
      const cursorToDotY = dotY - py;
      const cursorToDotDistance = Math.sqrt(
        cursorToDotX * cursorToDotX + cursorToDotY * cursorToDotY
      );

      const minDistance = 80;
      if (cursorToDotDistance < minDistance) {
        const pushStrength = 1 - cursorToDotDistance / minDistance;
        return pushStrength * pushMagnitude;
      }

      return 0;
    }
  );

  const pushAngle = useTransform(
    [pointerX, pointerY],
    ([px, py]: number[]) => {
      if (centerX === 0 || centerY === 0) return angle;

      const dotX = centerX + baseX;
      const dotY = centerY + baseY;

      const cursorToDotX = dotX - px;
      const cursorToDotY = dotY - py;

      return Math.atan2(cursorToDotY, cursorToDotX);
    }
  );

  const pushX = useTransform(
    [pushDistance, pushAngle],
    ([distance, ang]: number[]) => {
      return Math.cos(ang) * distance;
    }
  );

  const pushY = useTransform(
    [pushDistance, pushAngle],
    ([distance, ang]: number[]) => {
      return Math.sin(ang) * distance;
    }
  );

  const springPushX = useSpring(pushX, pushSpring);
  const springPushY = useSpring(pushY, pushSpring);

  const x = useTransform(springPushX, (val: number) => baseX + val);
  const y = useTransform(springPushY, (val: number) => baseY + val);

  const dotVariants = {
    default: {
      scale: 1,
    },
    hover: {
      scale: 1.5,
      transition: { duration: 0.13 },
    },
  };

  const ringVariants = {
    default: {
      opacity: 0,
    },
    hover: {
      opacity: 0.4,
      transition: { duration: 0.13 },
    },
  };

  const isSelected = selectedColor === color;

  return (
    <MotionBox
      className='color-dot'
      style={{
        x,
        y,
        backgroundColor: color,
        boxShadow: isSelected
          ? '0 0 0 3px #ffffff, 0 0 0 5px rgba(0, 0, 0, 0.4), 0 4px 12px rgba(0, 0, 0, 0.3)'
          : '0 2px 5px rgba(0, 0, 0, 0.25)',
        zIndex: isSelected ? 10 : 2,
        willChange: 'transform, background-color',
      } as any}
      variants={dotVariants}
      initial='default'
      whileHover='hover'
      whileTap={{ scale: 1.2 }}
      onClick={() => {
        if (selectedColor === color) {
          setSelectedColor(null);
        } else {
          setSelectedColor(color);
        }
      }}
      transition={{
        scale: { type: 'spring', damping: 30, stiffness: 200 },
      }}
    >
      <MotionBox className='color-dot-ring' variants={ringVariants} />
    </MotionBox>
  );
}

interface GradientCircleProps {
  index: number;
  totalInRing: number;
  centerX: number;
  centerY: number;
  pointerX: MotionValue<number>;
  pointerY: MotionValue<number>;
  containerRadius: number;
}

function GradientCircle({
  index,
  totalInRing,
  centerX,
  centerY,
  pointerX,
  pointerY,
  containerRadius,
}: GradientCircleProps) {
  const angle = calculateAngle(index, totalInRing);
  const baseRadius = containerRadius - 40;
  const { x: baseX, y: baseY } = calculateBasePosition(angle, baseRadius);
  const normalizedHue = calculateHue(angle);

  const gradient = `radial-gradient(circle, hsla(${normalizedHue}, 90%, 60%, 1) 0%, hsla(${normalizedHue}, 90%, 60%, 0) 66%)`;

  const proximity = useTransform(
    [pointerX, pointerY],
    ([px, py]: number[]) => {
      if (centerX === 0 || centerY === 0) return 0;

      const gradientX = centerX + baseX;
      const gradientY = centerY + baseY;

      const dx = px - gradientX;
      const dy = py - gradientY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      const maxDistance = 100;
      return Math.max(0, 1 - distance / maxDistance);
    }
  );

  const opacity = useTransform(proximity, (val: number) => 0.15 + val * 0.2);
  const scale = useTransform(proximity, (val: number) => 1 + val * 0.2);

  const springOpacity = useSpring(opacity, {
    damping: 30,
    stiffness: 100,
  });
  const springScale = useSpring(scale, {
    damping: 30,
    stiffness: 100,
  });

  return (
    <MotionBox
      className='gradient-circle'
      style={{
        x: baseX,
        y: baseY,
        opacity: springOpacity,
        scale: springScale,
        background: gradient,
        willChange: 'transform, opacity',
      } as any}
    />
  );
}

interface RadialMotionColorPickerProps {
  selectedColor: string | null;
  onSelectColor: (color: string | null) => void;
  pushMagnitude?: number;
  pushSpring?: SpringConfig;
}

export function RadialMotionColorPicker({
  selectedColor,
  onSelectColor,
  pushMagnitude = 5,
  pushSpring = {
    damping: 30,
    stiffness: 100,
  },
}: RadialMotionColorPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [{ centerX, centerY, radius }, setContainerDimensions] = useState({
    centerX: 0,
    centerY: 0,
    radius: 70,
  });

  const pointer = usePointerPosition();

  const updateDimensions = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerDimensions({
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
        radius: rect.width / 2,
      });
    }
  };

  useLayoutEffect(() => {
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    window.addEventListener('scroll', updateDimensions, true);
    return () => {
      window.removeEventListener('resize', updateDimensions);
      window.removeEventListener('scroll', updateDimensions, true);
    };
  }, []);

  const rings = [{ count: 1 }, { count: 6 }, { count: 12 }];

  const dots: Array<{
    ring: number;
    index: number;
    totalInRing: number;
  }> = [];

  rings.forEach((ring, ringIndex) => {
    for (let i = 0; i < ring.count; i++) {
      dots.push({
        ring: ringIndex,
        index: i,
        totalInRing: ring.count,
      });
    }
  });

  const originalStopValues: string[] = [];
  for (let i = 0; i <= 360; i += 30) {
    originalStopValues.push(`hsl(${i}, 90%, 60%)`);
  }

  const stopMotionValues = useRef(
    originalStopValues.map((value: string) => useMotionValue(value))
  ).current;

  useEffect(() => {
    if (selectedColor !== null) {
      for (const stopValue of stopMotionValues) {
        animate(stopValue, selectedColor, {
          duration: 0.2,
        });
      }
    } else {
      for (let i = 0; i < stopMotionValues.length; i++) {
        animate(stopMotionValues[i], originalStopValues[i], {
          duration: 0.2,
        });
      }
    }
  }, [selectedColor, stopMotionValues]);

  const gradientBackground = useTransform(
    stopMotionValues,
    (values: string[]) => {
      return `conic-gradient(from 0deg, ${values.join(', ')})`;
    }
  );

  const gradientScale = useMotionValue(1);

  useEffect(() => {
    if (selectedColor !== null) {
      animate(gradientScale, 1.1, {
        type: 'spring',
        damping: 18,
        stiffness: 220,
      });
    } else {
      animate(gradientScale, 1, {
        type: 'spring',
        damping: 20,
        stiffness: 200,
      });
    }
  }, [selectedColor, gradientScale]);

  return (
    <Box className='gradient-wrapper'>
      <Box className='background'>
        <MotionBox
          className='gradient-background'
          style={{
            background: gradientBackground,
            scale: gradientScale,
          } as any}
        />
        <MotionBox
          className='solid-background'
          animate={{
            scale: selectedColor !== null ? 0.9 : 0.98,
          }}
          transition={{
            type: 'spring',
            damping: 25,
            stiffness: 200,
          }}
        />
      </Box>

      <Box
        ref={containerRef}
        className='picker-background'
        onMouseEnter={updateDimensions}
      >
        {Array.from({ length: 6 }).map((_, index) => (
          <GradientCircle
            key={`gradient-${index}`}
            index={index}
            totalInRing={6}
            centerX={centerX}
            centerY={centerY}
            pointerX={pointer.x}
            pointerY={pointer.y}
            containerRadius={radius}
          />
        ))}

        {dots
          .slice()
          .reverse()
          .map((dot) => (
            <ColorDot
              key={`${dot.ring}-${dot.index}`}
              ring={dot.ring}
              index={dot.index}
              totalInRing={dot.totalInRing}
              centerX={centerX}
              centerY={centerY}
              pointerX={pointer.x}
              pointerY={pointer.y}
              radius={radius}
              pushMagnitude={pushMagnitude}
              pushSpring={pushSpring}
              selectedColor={selectedColor}
              setSelectedColor={onSelectColor}
            />
          ))}
      </Box>

      <StyleSheet />
    </Box>
  );
}

/**
 * Stylesheet for the physics-based Motion Color Picker
 */
function StyleSheet() {
  return (
    <style>{`
      .gradient-wrapper {
        position: relative;
        width: 140px;
        height: 140px;
        display: flex;
        align-items: center;
        justify-content: center;
        user-select: none;
        touch-action: none;
      }

      .background {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
      }

      .gradient-background {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        z-index: 0;
      }

      .solid-background {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        background-color: #0c121e;
        border-radius: 50%;
        z-index: 1;
      }

      .picker-background {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        border-radius: 50%;
        overflow: visible;
        z-index: 2;
      }

      .color-dot {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 32px;
        height: 32px;
        margin-top: -16px;
        margin-left: -16px;
        border-radius: 50%;
        cursor: pointer;
      }

      .color-dot-ring {
        position: absolute;
        inset: 0;
        border: 2px solid white;
        border-radius: 50%;
        mix-blend-mode: overlay;
        pointer-events: none;
      }

      .gradient-circle {
        position: absolute;
        top: 50%;
        left: 50%;
        width: 150px;
        height: 150px;
        margin-top: -75px;
        margin-left: -75px;
        border-radius: 50%;
        pointer-events: none;
        mix-blend-mode: screen;
      }
    `}</style>
  );
}

/**
 * ==============   Apple-Style Full Motion Color Picker Component   ================
 */

export const DEFAULT_SWATCHES = [
  { name: 'HiAcuity Emerald', hex: '#038912' },
  { name: 'Deep Forest', hex: '#093725' },
  { name: 'Apple Sapphire', hex: '#0071e3' },
  { name: 'Royal Indigo', hex: '#4f46e5' },
  { name: 'Electric Violet', hex: '#8b5cf6' },
  { name: 'Apple Rose', hex: '#ff2d55' },
  { name: 'Sunset Coral', hex: '#ff643d' },
  { name: 'Amber Gold', hex: '#f59e0b' },
  { name: 'Teal Cyan', hex: '#06b6d4' },
  { name: 'Space Slate', hex: '#1e293b' },
];

export interface SwatchItem {
  name: string;
  hex: string;
}

export interface MotionColorPickerProps {
  color?: string;
  onChange: (newColor: string) => void;
  isEditing?: boolean;
  label?: string;
  description?: string;
  presetColors?: SwatchItem[];
  defaultColor?: string;
  popoverAlign?: 'left' | 'right';
}

export const MotionColorPicker: React.FC<MotionColorPickerProps> = ({
  color,
  onChange,
  isEditing = true,
  label = 'Brand Color',
  presetColors = DEFAULT_SWATCHES,
  defaultColor = DEFAULT_PRIMARY_COLOR,
  popoverAlign = 'left',
}) => {
  const currentColor = isValidHex(color || '') ? normalizeHex(color || '') : defaultColor;
  const [isOpen, setIsOpen] = useState(false);
  const [inputHex, setInputHex] = useState(currentColor);
  const [copied, setCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const hueSliderRef = useRef<HTMLDivElement>(null);

  const isCustomColor =
    normalizeHex(currentColor).toLowerCase() !==
    normalizeHex(defaultColor).toLowerCase();

  const handleResetToDefault = () => {
    onChange(defaultColor);
  };

  useEffect(() => {
    setInputHex(currentColor);
  }, [currentColor]);

  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const contrastText = getContrastTextColor(currentColor);
  const contrastRatioWhite = getContrastRatio(currentColor, '#ffffff');
  const contrastRatioDark = getContrastRatio(currentColor, '#0f172a');
  const activeContrastRatio =
    contrastText === '#ffffff' ? contrastRatioWhite : contrastRatioDark;

  const currentHsl = hexToHsl(currentColor);

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputHex(val);
    if (isValidHex(val.trim())) {
      onChange(normalizeHex(val.trim()));
    }
  };

  const handleCopyHex = () => {
    navigator.clipboard.writeText(currentColor.toUpperCase());
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleSelectMotionDot = (hslColor: string | null) => {
    if (!hslColor) {
      onChange(defaultColor);
      return;
    }
    const hex = hslToHex(hslColor);
    onChange(hex);
  };

  const handleHueBarInteraction = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!hueSliderRef.current) return;
    const rect = hueSliderRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
    const hue = (x / rect.width) * 360;

    const newHex = hslToHex(hue, 85, 45);
    onChange(newHex);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        display: 'inline-block',
        width: '100%',
        zIndex: isOpen ? 50 : 10,
      }}
    >
      {/* Apple-styled Trigger Bar */}
      <MotionBox
        whileHover={isEditing ? { scale: 1.005, translateY: -1 } : {}}
        whileTap={isEditing ? { scale: 0.995 } : {}}
        onClick={() => isEditing && setIsOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1.5,
          p: 1.75,
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(226, 232, 240, 0.9)',
          borderRadius: 4,
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          cursor: isEditing ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          '&:hover': isEditing
            ? {
                borderColor: '#CBD5E1',
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
              }
            : {},
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          {/* Animated Color Swatch with Gloss Effect */}
          <MotionBox
            layout
            sx={{
              position: 'relative',
              width: 44,
              height: 44,
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 700,
              fontSize: '0.75rem',
              overflow: 'hidden',
              flexShrink: 0,
              backgroundColor: currentColor,
              color: contrastText,
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(to top right, rgba(0,0,0,0.1), transparent, rgba(255,255,255,0.2))',
                pointerEvents: 'none',
              }}
            />
            <Typography
              component='span'
              sx={{
                position: 'relative',
                zIndex: 1,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: 'inherit',
              }}
            >
              Aa
            </Typography>
          </MotionBox>

          <Box sx={{ textAlign: 'left' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant='subtitle2'
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 700,
                  color: '#1E293B',
                  letterSpacing: '-0.01em',
                }}
              >
                {label}
              </Typography>
              {isEditing && (
                <Typography
                  component='span'
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    color: '#059669',
                    bgcolor: '#ECFDF5',
                    px: 1,
                    py: 0.25,
                    borderRadius: 10,
                    border: '1px solid #D1FAE5',
                  }}
                >
                  Customize
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 0.25,
              }}
            >
              <Typography
                variant='caption'
                sx={{
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  color: '#334155',
                  fontSize: '0.75rem',
                }}
              >
                {currentColor.toUpperCase()}
              </Typography>
              <Typography
                component='span'
                sx={{ color: '#CBD5E1', fontSize: '0.75rem' }}
              >
                •
              </Typography>
              <Typography
                variant='caption'
                sx={{ color: '#94A3B8', fontSize: '0.6875rem' }}
              >
                {currentHsl.hslString}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {/* WCAG Contrast badge */}
          <Box
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 10,
              fontSize: '0.625rem',
              fontWeight: 700,
              letterSpacing: '-0.01em',
              border: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              backgroundColor:
                contrastText === '#ffffff' ? '#0F172A' : '#F8FAFC',
              color: contrastText === '#ffffff' ? '#FFFFFF' : '#0F172A',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <Box
              component='span'
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: '#34D399',
                display: 'inline-block',
              }}
            />
            <Typography component='span' sx={{ fontSize: 'inherit', fontWeight: 'inherit', color: 'inherit' }}>
              WCAG {activeContrastRatio.toFixed(1)}:1
            </Typography>
          </Box>

          {isEditing && (
            <MotionBox
              animate={{ rotate: isOpen ? 180 : 0 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              sx={{
                p: 0.75,
                borderRadius: 2,
                bgcolor: 'rgba(241, 245, 249, 0.8)',
                color: '#64748B',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ChevronDown size={16} />
            </MotionBox>
          )}
        </Box>
      </MotionBox>

      {/* Apple-styled Motion Color Picker Popover */}
      <AnimatePresence>
        {isOpen && isEditing && (
          <MotionBox
            initial={{ opacity: 0, y: 10, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.96 }}
            transition={{ type: 'spring', damping: 25, stiffness: 320 }}
            sx={{
              position: 'absolute',
              zIndex: 100,
              ...(popoverAlign === 'right'
                ? { right: 0, left: 'auto' }
                : { left: 0, right: 'auto' }),
              mt: 1.25,
              width: 360,
              maxWidth: '92vw',
              bgcolor: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(24px)',
              border: '1px solid rgba(226, 232, 240, 0.9)',
              borderRadius: 5,
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              p: 2.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflow: 'hidden',
            }}
          >
            {/* Popover Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #F1F5F9',
                pb: 1.5,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '50%',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    backgroundColor: currentColor,
                  }}
                />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography
                    variant='subtitle2'
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: '#1E293B',
                    }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{ fontSize: '0.6875rem', color: '#94A3B8' }}
                  >
                    Physics Color Spectrum
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isCustomColor && isEditing && (
                  <Button
                    size='small'
                    onClick={handleResetToDefault}
                    title={`Reset to default (${defaultColor.toUpperCase()})`}
                    startIcon={<RotateCcw size={12} />}
                    sx={{
                      px: 1.25,
                      py: 0.5,
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      borderRadius: 2,
                      bgcolor: '#F1F5F9',
                      color: '#475569',
                      textTransform: 'none',
                      boxShadow: 'none',
                      minWidth: 'auto',
                      '&:hover': { bgcolor: '#E2E8F0', color: '#1E293B' },
                    }}
                  >
                    Reset
                  </Button>
                )}
                <IconButton
                  size='small'
                  onClick={() => setIsOpen(false)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: '#F1F5F9',
                    color: '#64748B',
                    '&:hover': { bgcolor: '#E2E8F0', color: '#1E293B' },
                  }}
                >
                  <X size={14} />
                </IconButton>
              </Box>
            </Box>

            {/* Clean Light Surface for Color Wheel */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 2,
                px: 1.5,
                bgcolor: 'rgba(248, 250, 252, 0.9)',
                borderRadius: 4,
                border: '1px solid rgba(226, 232, 240, 0.8)',
                boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 10, p: 0.5 }}>
                <RadialMotionColorPicker
                  selectedColor={currentHsl.hslString}
                  onSelectColor={handleSelectMotionDot}
                />
              </Box>
              <Typography
                variant='caption'
                sx={{
                  mt: 1.25,
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  color: '#94A3B8',
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  position: 'relative',
                  zIndex: 10,
                }}
              >
                Hover dots to repel with physics
              </Typography>
            </Box>

            {/* Curated Swatches */}
            <Box sx={{ textAlign: 'left' }}>
              <Typography
                variant='caption'
                sx={{
                  fontSize: '0.625rem',
                  fontWeight: 700,
                  color: '#94A3B8',
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  display: 'block',
                  mb: 1,
                }}
              >
                Recommended Presets
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(5, 1fr)',
                  gap: 1,
                }}
              >
                {presetColors.map((item) => {
                  const isSelected =
                    currentColor.toLowerCase() === item.hex.toLowerCase();
                  return (
                    <MotionBox
                      key={item.hex}
                      onClick={() => onChange(item.hex)}
                      title={item.name}
                      whileHover={{ scale: 1.12 }}
                      whileTap={{ scale: 0.92 }}
                      sx={{
                        position: 'relative',
                        width: 40,
                        height: 40,
                        borderRadius: 3,
                        border: '1px solid',
                        borderColor: isSelected
                          ? '#10B981'
                          : 'rgba(0, 0, 0, 0.1)',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: item.hex,
                        outline: isSelected ? '2px solid #10B981' : 'none',
                        outlineOffset: isSelected ? '2px' : '0',
                        boxShadow: isSelected
                          ? '0 4px 12px rgba(0,0,0,0.15)'
                          : '0 1px 3px rgba(0,0,0,0.06)',
                        transition: 'all 0.15s ease',
                      }}
                    >
                      {isSelected && (
                        <Check
                          size={18}
                          color={getContrastTextColor(item.hex)}
                          strokeWidth={3}
                        />
                      )}
                    </MotionBox>
                  );
                })}
              </Box>
            </Box>

            {/* Hue Spectrum Slider */}
            <Box sx={{ textAlign: 'left' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.75,
                }}
              >
                <Typography
                  variant='caption'
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    color: '#64748B',
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}
                >
                  Hue Spectrum
                </Typography>
                <Typography
                  variant='caption'
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.625rem',
                    color: '#64748B',
                  }}
                >
                  {currentHsl.h}°
                </Typography>
              </Box>
              <Box
                ref={hueSliderRef}
                onClick={handleHueBarInteraction}
                sx={{
                  position: 'relative',
                  height: 20,
                  borderRadius: 10,
                  cursor: 'pointer',
                  overflow: 'hidden',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.1)',
                  background:
                    'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
                }}
              >
                <MotionBox
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    width: 20,
                    ml: '-10px',
                    borderRadius: '50%',
                    border: '2px solid #FFFFFF',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    bgcolor: 'transparent',
                    pointerEvents: 'none',
                  }}
                  animate={{ left: `${(currentHsl.h / 360) * 100}%` }}
                  transition={{ type: 'spring', damping: 25, stiffness: 280 }}
                />
              </Box>
            </Box>

            {/* HEX Input & Copy Action */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                pt: 1.5,
                borderTop: '1px solid #F1F5F9',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderRadius: 3,
                  px: 1.5,
                  py: 0.5,
                  '&:focus-within': {
                    borderColor: '#10B981',
                    bgcolor: '#FFFFFF',
                    boxShadow: '0 0 0 2px rgba(16, 185, 129, 0.2)',
                  },
                }}
              >
                <Typography
                  component='span'
                  sx={{
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    color: '#94A3B8',
                    mr: 1,
                  }}
                >
                  HEX
                </Typography>
                <InputBase
                  value={inputHex}
                  onChange={handleHexInputChange}
                  placeholder='#038912'
                  fullWidth
                  sx={{
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    color: '#1E293B',
                  }}
                />
              </Box>

              <Button
                size='small'
                onClick={handleCopyHex}
                startIcon={
                  copied ? (
                    <Check size={14} color='#047857' />
                  ) : (
                    <Copy size={14} color='#64748B' />
                  )
                }
                sx={{
                  px: 1.75,
                  py: 0.75,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: copied ? '#047857' : '#334155',
                  bgcolor: copied ? '#D1FAE5' : '#F1F5F9',
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: copied ? '#A7F3D0' : '#E2E8F0',
                  },
                }}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </Box>
          </MotionBox>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default MotionColorPicker;
